import React from 'react';
import { configureStore } from '@reduxjs/toolkit';
import { Provider } from 'react-redux';
import { persistReducer, persistStore } from 'redux-persist';
import { PersistGate } from 'redux-persist/integration/react';
import storage from 'redux-persist/lib/storage';
import {
  useAmsStore,
  useBranchStore,
  useConfirmJobsHistoryStore,
  useCustomerInfoStore,
  useDispatch,
  useEmployeeStore,
  useKpiDashboardStore,
  useSchedulingStore,
  useSelector,
  useUserStore,
  useVehicleStore,
} from './hooks';
import branchReducer from './reducers/branch.reducer';
import crewsReducer from './reducers/crews.reducer';
import { confirmJobHistorySlice } from './reducers/customer-info-job-history.reducer';
import { customerInfoSlice } from './reducers/customer-info.reducer';
import employeeReducer from './reducers/employee.reducer';
import { jobSlice } from './reducers/jobs.reducer';
import kpiBucketReducer from './reducers/kpi-dashboard.reducer';
import schedulingReducer from './reducers/scheduling.reducer';
import { userSlice } from './reducers/user.reducer';
import vehicleReducer from './reducers/vehicle.reducer';

const persistConfig = {
  key: 'root',
  storage,
};

const persistedReducer = persistReducer(persistConfig, userSlice.reducer);

const { signIn, loggedOut } = userSlice.actions;

// global store.
const store = configureStore({
  reducer: {
    customerInfo: customerInfoSlice.reducer,
    user: persistedReducer,
    crew: crewsReducer,
    jobs: jobSlice.reducer,
    employee: employeeReducer,
    branch: branchReducer,
    vehicle: vehicleReducer,
    jobHistory: confirmJobHistorySlice.reducer,
    kpiBucket: kpiBucketReducer,
    scheduling: schedulingReducer,
  },
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: false,
    }),
});
export type AppDispatch = typeof store.dispatch;
const persistor = persistStore(store);

export const useStore = () => {
  const userStore = useSelector((state) => state);
  const dispatch = useDispatch();
  return {
    signIn,
    loggedOut,
    dispatch,
    userStore,
    useCustomerInfoStore,
  };
};

export const StoreProvider = ({ children }: { children: React.ReactNode }) => {
  return (
    <Provider store={store}>
      <PersistGate loading={null} persistor={persistor}>
        {children}
      </PersistGate>
    </Provider>
  );
};

export {
  useAmsStore,
  useBranchStore,
  useConfirmJobsHistoryStore,
  useCustomerInfoStore,
  useDispatch,
  useEmployeeStore,
  useKpiDashboardStore,
  useSchedulingStore,
  useSelector,
  useUserStore,
  useVehicleStore,
};
